import type { ReactNode } from 'react';
import { Drawer } from '../drawer/drawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../dialog/dialog';
import { useBreakpoint } from '../../hooks/use_breakpoint';
import { cn } from '../../lib/cn';

/**
 * Named max-width sizes for the tablet/desktop `Dialog` presentation.
 *
 * This MUST stay a static union mapped to literal `max-w-*` class strings
 * (never a runtime-interpolated `max-w-[${value}]`) -- Tailwind's static
 * source-scanner only ever sees literal class strings in scanned files, so
 * an interpolated class is invisible to it and generates no CSS at all.
 * Worse, `cn()`/twMerge classifies any `max-w-[...]` into the same conflict
 * group as `DialogContent`'s built-in `max-w-lg` and drops the earlier
 * class, so an interpolated value silently leaves the dialog with NO
 * max-width cap (full-viewport-width). Every value below is a literal
 * string present verbatim in this file's source, so Tailwind always
 * generates CSS for it regardless of which one is selected at runtime.
 */
export type AdaptiveDialogMaxWidth = 'md' | 'lg' | 'xl' | '2xl' | '3xl';

const MAX_WIDTH_CLASS_NAMES: Record<AdaptiveDialogMaxWidth, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
};

export interface AdaptiveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Title passed through to the underlying Drawer or Dialog title */
  title?: string;
  /** Description shown below the title in dialog mode */
  description?: string;
  children: ReactNode;
  /** Optional footer content */
  footer?: ReactNode;
  /**
   * Additional className applied to the underlying surface -- the Drawer
   * panel on phone, `DialogContent` on tablet/desktop.
   */
  className?: string;
  /**
   * Named max-width for the tablet/desktop `Dialog` presentation only.
   * Ignored on phone -- the bottom sheet is always full-width by design.
   * A static allow-list (see `AdaptiveDialogMaxWidth`) by design -- see the
   * comment on `MAX_WIDTH_CLASS_NAMES` above for why this can't be a raw
   * CSS-value string.
   */
  maxWidth?: AdaptiveDialogMaxWidth;
  /**
   * Overrides the default `data-testid` on the tablet/desktop
   * `DialogContent` (`'adaptive-dialog-content'`). The phone Drawer panel
   * keeps its own fixed `'drawer-panel'` testid regardless.
   */
  dataTestId?: string;
  /**
   * Passed straight through to the phone Drawer's own `initialFocus` prop
   * (see that component's doc comment) -- ignored on tablet/desktop, where
   * Radix's Dialog always focuses its own first focusable element
   * regardless. Set to `'close'` when this dialog's body's first control
   * performs a consequential/mutating action, so a stray Enter at open
   * can't trigger it.
   */
  initialFocus?: 'body' | 'close';
}

/**
 * Renders a bottom-sheet (Drawer, `side="bottom"`) on phone and a centered
 * dialog on tablet/desktop, selected via useBreakpoint(). Business logic
 * and view-model state live in the caller; this component only handles
 * the presentation fork.
 */
export function AdaptiveDialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  maxWidth,
  dataTestId,
  initialFocus,
}: AdaptiveDialogProps) {
  const { device } = useBreakpoint();

  if (device === 'phone') {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={title}
        description={description}
        footer={footer}
        className={className}
        side="bottom"
        initialFocus={initialFocus}
      >
        {children}
      </Drawer>
    );
  }

  // Looked up from the static `MAX_WIDTH_CLASS_NAMES` map (never built via
  // string interpolation -- see that map's doc comment). Routed through
  // Tailwind's `max-w-*` utility (via `cn()`'s twMerge conflict resolution)
  // rather than an inline `style` prop -- DialogContent forwards any
  // caller-supplied `style` with `{...props}` *after* setting its own base
  // `style` object, so a raw `style` prop here would silently replace (not
  // merge with) DialogContent's border/shadow/overflow styling. `className`
  // merges safely because DialogContent runs it through `cn()`. Matches the
  // existing call-site convention (e.g.
  // `apps/ledger-web/src/components/layout/top_nav_user_menu.tsx`'s
  // `className="sm:max-w-md"`). `maxWidthClassName` is `undefined` both when
  // `maxWidth` is omitted and for any value outside the union (defensive --
  // TS already rejects the latter at compile time) -- either way
  // DialogContent's own default `max-w-lg` is left uncontested, never a
  // full-width dialog.
  const maxWidthClassName =
    maxWidth !== undefined ? MAX_WIDTH_CLASS_NAMES[maxWidth] : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        data-testid={dataTestId ?? 'adaptive-dialog-content'}
        className={cn(maxWidthClassName, className)}
      >
        {(title !== undefined || description !== undefined) && (
          <DialogHeader>
            {title !== undefined && <DialogTitle>{title}</DialogTitle>}
            {description !== undefined && (
              <DialogDescription>{description}</DialogDescription>
            )}
          </DialogHeader>
        )}
        {children}
        {footer !== undefined && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
